import {
  Button,
  SegmentedControl,
  CurrencyInput,
  DatePicker,
  Input,
  Modal,
  Select,
  StatusPill,
  Textarea,
  formatCivilDate,
  formatCurrency,
  useSnackbar,
  IconButton,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  Paperclip,
  ShieldAlert,
  Upload,
  X,
  CircleCheck,
  Undo2,
  Eye,
  Ban,
} from 'lucide-react';
import { useRef, useState, type DragEvent } from 'react';
import { RequestAlert } from '../../app/data-view.js';
import { civilDateOf } from '../../lib/time-zone.js';
import {
  getApiError,
  salesApi,
  type OnlineCharge,
  type Sale,
  type SalePayment,
  type SalePaymentMethod,
} from '../../lib/api.js';
import { AWAITING, paymentTotals } from './sale-payment-totals.js';
import { AmountField } from './amount-field.js';
import { FormFrame } from './payment-form-frame.js';
import { CancelChargeForm, OnlineChargeForm, OnlineChargePanel } from './online-charge.js';
import {
  onlinePaymentState,
  onlinePaymentTitle,
  useOnlineCharges,
  PAYMENT_REFRESH_MS,
  tracksOnlinePayments,
} from './online-charge-state.js';
import { basisPointsOf, percentOf, type AmountMode } from './sale-cart.js';

const ACCEPTED = 'application/pdf,image/jpeg,image/png,image/webp';
const METHOD_LABELS: Record<SalePaymentMethod, string> = {
  CASH: 'Dinheiro',
  PIX: 'PIX',
  CARD: 'Cartão',
  OTHER: 'Outro',
  CREDIT: 'Crédito do cliente',
  ONLINE: 'Cobrança online',
};
type Mode =
  | { kind: 'register' }
  | { kind: 'credit' }
  | { kind: 'charge' }
  | { kind: 'view-charge'; chargeId: string }
  | { kind: 'cancel-charge'; charge: OnlineCharge }
  | { kind: 'confirm'; payment: SalePayment }
  | { kind: 'reverse'; payment: SalePayment }
  | null;

export function SalePaymentsDialog({
  sale: initial,
  initialCharge = false,
  canManage,
  financeContracted,
  today,
  onChanged,
  onClose,
}: {
  sale: Sale;
  initialCharge?: boolean;
  canManage: boolean;
  financeContracted: boolean;
  today: string;
  onChanged: () => Promise<unknown>;
  onClose: () => void;
}) {
  const snackbar = useSnackbar();
  const queryClient = useQueryClient();
  const saleKey = ['company', 'sales', initial.id, 'detail'] as const;
  const liveSale = useQuery({
    queryKey: saleKey,
    queryFn: () => salesApi.get(initial.id),
    initialData: initial,
    staleTime: PAYMENT_REFRESH_MS,
    refetchInterval: (query) =>
      tracksOnlinePayments(query.state.data ?? initial) ? PAYMENT_REFRESH_MS : false,
    retry: false,
  });
  const sale = liveSale.data;
  const setSale = (next: Sale) => queryClient.setQueryData(saleKey, next);
  const [selectedMode, setMode] = useState<Mode>(
    initialCharge && canManage ? { kind: 'charge' } : null,
  );
  const settings = useQuery({
    queryKey: ['company', 'sales', 'settings'],
    queryFn: () => salesApi.settings(),
    retry: false,
  });
  const credit = useQuery({
    queryKey: ['company', 'sales', sale.id, 'credit'],
    queryFn: () => salesApi.credit(sale.id),
    enabled: Boolean(sale.clientId),
    refetchInterval: tracksOnlinePayments(sale) ? PAYMENT_REFRESH_MS : false,
    retry: false,
  });
  const online = useOnlineCharges(sale, canManage && initial.status === 'CONFIRMED');
  const selectedCharge =
    selectedMode?.kind === 'view-charge'
      ? [...online.byPayment.values()].find((charge) => charge.id === selectedMode.chargeId)
      : undefined;
  const chargeFinished =
    selectedCharge &&
    (selectedCharge.status !== 'WAITING' ||
      sale.payments.some(
        (payment) => payment.id === selectedCharge.salePaymentId && !AWAITING.has(payment.status),
      ));
  const mode = selectedMode?.kind === 'view-charge' && chargeFinished ? null : selectedMode;
  const required = settings.data?.receiptRequiredMethods ?? [];
  const totals = paymentTotals(sale);
  const timeZone = sale.timeZone ?? 'UTC';
  const editable = canManage && sale.status === 'CONFIRMED';

  async function applied(next: Sale, message: string) {
    setSale(next);
    setMode(null);
    snackbar.success(message);
    if (sale.clientId) await credit.refetch();
    await onChanged();
  }

  /// A cobrança mudou fora da venda: a venda é relida para os totais e as linhas acompanharem.
  async function chargeChanged(charge: OnlineCharge, message: string, keepOpen: boolean) {
    setSale(await salesApi.get(sale.id));
    await online.refetch();
    setMode(
      keepOpen && charge.status === 'WAITING' ? { kind: 'view-charge', chargeId: charge.id } : null,
    );
    snackbar.success(message);
    await onChanged();
  }
  const charging = online.status?.available === true && Boolean(sale.clientId);
  const viewing =
    mode?.kind === 'view-charge'
      ? [...online.byPayment.values()].find((charge) => charge.id === mode.chargeId)
      : undefined;

  if (mode?.kind === 'credit' && credit.data && !credit.error)
    return (
      <CreditForm
        sale={sale}
        today={today}
        availableCents={credit.data.availableCents}
        remainingCents={totals.registrableCents}
        onCancel={() => setMode(null)}
        onSaved={(next) => applied(next, 'Crédito aplicado. O saldo restante continua disponível.')}
      />
    );

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={`Pagamentos da venda ${sale.number}`}
      description={`${sale.clientDisplayName ?? 'Venda de balcão'} · ${sale.businessUnitName}`}
      footer={
        <div className="flex flex-wrap justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
          {mode?.kind === 'credit' && (credit.error || credit.isLoading) ? (
            <Button variant="outline" onClick={() => setMode(null)}>
              Voltar
            </Button>
          ) : null}
          {editable && !mode && sale.clientId && totals.registrableCents > 0 ? (
            <Button variant="outline" onClick={() => setMode({ kind: 'credit' })}>
              Usar crédito do cliente
            </Button>
          ) : null}
          {editable && !mode && (totals.registrableCents > 0 || sale.clientId) ? (
            <Button
              variant={charging && totals.registrableCents > 0 ? 'outline' : undefined}
              onClick={() => setMode({ kind: 'register' })}
            >
              Registrar recebimento
            </Button>
          ) : null}
          {editable && !mode && charging && totals.registrableCents > 0 ? (
            <Button onClick={() => setMode({ kind: 'charge' })}>Gerar cobrança</Button>
          ) : null}
        </div>
      }
    >
      <div className="grid gap-4">
        <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {(
            [
              ['Total', sale.netTotalCents],
              ['Aplicado na venda', totals.confirmedCents],
              ['Aguardando', totals.awaitingCents],
              ['Em aberto', totals.openCents],
            ] as const
          ).map(([label, cents]) => (
            <div key={label} className="rounded-xl bg-surface-elevated px-3 py-2">
              <dt className="text-xs text-content-muted">{label}</dt>
              <dd className="font-semibold tabular-nums">{formatCurrency(cents, sale.currency)}</dd>
            </div>
          ))}
        </dl>

        {sale.clientId && mode?.kind !== 'credit' ? (
          <section
            aria-label="Crédito do cliente"
            className="rounded-xl border border-brand-500/30 p-3"
          >
            <p className="text-sm font-semibold">Crédito disponível para esta venda</p>
            {credit.isLoading ? (
              <p role="status">Consultando crédito do cliente…</p>
            ) : credit.error ? (
              <p role="alert">Não foi possível consultar o crédito do cliente.</p>
            ) : credit.data ? (
              <p className="font-semibold tabular-nums">
                {formatCurrency(credit.data.availableCents, credit.data.currency)}
              </p>
            ) : null}
            <p className="text-xs text-content-muted">
              {sale.businessUnitName} · O valor só será descontado quando você aplicar o crédito.
            </p>
          </section>
        ) : null}

        {sale.payments.length === 0 ? (
          <p className="rounded-xl border border-surface-border px-4 py-5 text-center text-sm text-content-muted">
            Nenhum pagamento lançado ainda.
          </p>
        ) : (
          <ul aria-label="Pagamentos" className="grid gap-2">
            {sale.payments.map((payment) =>
              payment.method === 'ONLINE' ? (
                <OnlinePaymentRow
                  key={payment.id}
                  sale={sale}
                  payment={payment}
                  charge={online.byPayment.get(payment.id)}
                  editable={editable && !mode}
                  onView={(charge) => setMode({ kind: 'view-charge', chargeId: charge.id })}
                  onCancel={(charge) => setMode({ kind: 'cancel-charge', charge })}
                />
              ) : (
                <PaymentRow
                  key={payment.id}
                  sale={sale}
                  payment={payment}
                  timeZone={timeZone}
                  canManage={canManage}
                  editable={editable && !mode}
                  onConfirm={() => setMode({ kind: 'confirm', payment })}
                  onReverse={() => setMode({ kind: 'reverse', payment })}
                  onAttached={(next) => applied(next, 'Comprovante anexado.')}
                />
              ),
            )}
          </ul>
        )}

        {mode?.kind === 'credit' ? (
          credit.isLoading ? (
            <p role="status">Consultando crédito do cliente…</p>
          ) : credit.error ? (
            <RequestAlert message={getApiError(credit.error)} />
          ) : null
        ) : null}
        {editable &&
        !mode &&
        online.status?.contracted &&
        !online.status.available &&
        totals.registrableCents > 0 ? (
          <p className="rounded-xl border border-surface-border px-3 py-2 text-xs text-content-muted">
            Para cobrar por Pix ou link e confirmar sozinho, conecte a conta do Asaas em
            Configurações → Vendas → Cobrança online.
          </p>
        ) : null}
        {mode?.kind === 'charge' ? (
          <OnlineChargeForm
            sale={sale}
            today={today}
            dueDays={online.status?.dueDays ?? 2}
            suggestedCents={totals.registrableCents}
            availableCreditCents={credit.data?.availableCents ?? 0}
            onCreditApplied={async (next) => {
              setSale(next);
              if (paymentTotals(next).registrableCents === 0) setMode(null);
              snackbar.success('Crédito aplicado. O valor restante da cobrança foi recalculado.');
              await credit.refetch();
              await onChanged();
            }}
            onCancel={() => setMode(null)}
            onCreated={(charge) =>
              chargeChanged(
                charge,
                `Cobrança de ${formatCurrency(charge.amountCents, sale.currency)} gerada.`,
                true,
              )
            }
          />
        ) : null}
        {mode?.kind === 'view-charge' && viewing ? (
          <OnlineChargePanel
            sale={sale}
            charge={viewing}
            canManage={editable}
            onBack={() => setMode(null)}
            onChanged={(charge, message) => chargeChanged(charge, message, true)}
          />
        ) : null}
        {mode?.kind === 'cancel-charge' ? (
          <CancelChargeForm
            sale={sale}
            charge={mode.charge}
            onCancel={() => setMode(null)}
            onCanceled={(charge) =>
              chargeChanged(charge, 'Cobrança cancelada. O valor voltou para em aberto.', false)
            }
          />
        ) : null}
        {mode?.kind === 'register' ? (
          <RegisterForm
            sale={sale}
            today={today}
            required={required}
            financeContracted={financeContracted}
            suggestedCents={totals.registrableCents}
            onCancel={() => setMode(null)}
            onSaved={(next, cents) =>
              applied(
                next,
                `Recebimento de ${formatCurrency(cents, sale.currency)} registrado na venda ${sale.number}.`,
              )
            }
          />
        ) : null}
        {mode?.kind === 'confirm' ? (
          <ConfirmForm
            sale={sale}
            payment={mode.payment}
            today={today}
            required={required.includes(mode.payment.method)}
            onCancel={() => setMode(null)}
            onSaved={(next, cents) =>
              applied(next, `Pagamento de ${formatCurrency(cents, sale.currency)} confirmado.`)
            }
          />
        ) : null}
        {mode?.kind === 'reverse' ? (
          <ReverseForm
            sale={sale}
            payment={mode.payment}
            onCancel={() => setMode(null)}
            onSaved={(next) => applied(next, 'Pagamento estornado.')}
          />
        ) : null}
      </div>
    </Modal>
  );
}

const REFUND_METHOD_LABELS = { CASH: 'dinheiro', PIX: 'Pix', OTHER: 'outro meio' } as const;

function PaymentRow({
  sale,
  payment,
  timeZone,
  canManage,
  editable,
  onConfirm,
  onReverse,
  onAttached,
}: {
  sale: Sale;
  payment: SalePayment;
  timeZone: string;
  canManage: boolean;
  editable: boolean;
  onConfirm: () => void;
  onReverse: () => void;
  onAttached: (sale: Sale) => Promise<void>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string>();
  const reversed = payment.status === 'REVERSED';
  const paidOn = payment.paidAt ? formatCivilDate(civilDateOf(payment.paidAt, timeZone)) : null;
  const detail = reversed
    ? `Estornado${payment.reversedByName ? ` por ${payment.reversedByName}` : ''} · motivo: ${payment.reversalReason ?? ''}`
    : AWAITING.has(payment.status)
      ? payment.status === 'AUTHORIZED'
        ? 'Autorizado na venda, aguardando confirmação'
        : 'Combinado na venda, aguardando confirmação'
      : `${payment.method === 'CREDIT' ? 'Crédito aplicado' : 'Recebido'}${paidOn ? ` em ${paidOn}` : ''}${
          payment.confirmedByName ? ` · lançado por ${payment.confirmedByName}` : ''
        }`;

  async function attach(file: File | undefined) {
    if (!file) return;
    setError(undefined);
    try {
      await onAttached(await salesApi.attachPaymentReceipt(sale.id, payment.id, file));
    } catch (value) {
      setError(getApiError(value));
    }
  }

  return (
    <li className="grid gap-2 rounded-xl border border-surface-border px-3 py-2.5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <strong className="text-sm">
            {`${METHOD_LABELS[payment.method]}${
              payment.card ? ` · ${payment.card.installmentCount}×` : ''
            }`}
          </strong>
          <p className="text-xs text-content-muted">{detail}</p>
          {payment.receivedAmountCents != null ? (
            <p className="text-xs text-content-secondary">
              Recebido: {formatCurrency(payment.receivedAmountCents, sale.currency)} · Na venda:{' '}
              {formatCurrency(payment.amountCents, sale.currency)} · Excedente{' '}
              {reversed ? 'desfeito' : 'em crédito'}:{' '}
              {formatCurrency(payment.receivedAmountCents - payment.amountCents, sale.currency)}.
              Motivo: {payment.excessCreditReason}
            </p>
          ) : null}
          {payment.externalReference ? (
            <p className="text-xs text-content-faint">Identificação: {payment.externalReference}</p>
          ) : null}
        </div>
        <span
          className={
            reversed ? 'tabular-nums line-through opacity-70' : 'tabular-nums font-semibold'
          }
        >
          {formatCurrency(payment.receivedAmountCents ?? payment.amountCents, sale.currency)}
        </span>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill
            tone={reversed ? 'neutral' : AWAITING.has(payment.status) ? 'warning' : 'positive'}
          >
            {reversed
              ? 'Estornado'
              : AWAITING.has(payment.status)
                ? 'Aguardando confirmação'
                : 'Confirmado'}
          </StatusPill>
          {payment.receipt ? (
            <a
              href={salesApi.paymentReceiptUrl(payment.receipt.id)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-brand-300 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
            >
              <Paperclip className="size-3.5" aria-hidden="true" />
              {payment.receipt.fileName}
            </a>
          ) : canManage && payment.status === 'CONFIRMED' && payment.method !== 'CREDIT' ? (
            <>
              <IconButton
                label="Anexar comprovante"
                icon={<Paperclip />}
                onClick={() => inputRef.current?.click()}
              />
              <input
                ref={inputRef}
                type="file"
                accept={ACCEPTED}
                className="sr-only"
                aria-label={`Comprovante do pagamento de ${formatCurrency(payment.receivedAmountCents ?? payment.amountCents, sale.currency)}`}
                onChange={(event) => void attach(event.target.files?.[0])}
              />
            </>
          ) : null}
          {payment.receipt?.scanStatus === 'NOT_SCANNED' ? (
            <span className="inline-flex items-center gap-1 text-xs text-warning">
              <ShieldAlert className="size-3.5" aria-hidden="true" />
              Sem varredura de antivírus neste ambiente
            </span>
          ) : null}
        </div>
        {editable && AWAITING.has(payment.status) ? (
          <IconButton label="Confirmar" icon={<CircleCheck />} onClick={onConfirm} />
        ) : editable && payment.status === 'CONFIRMED' ? (
          <IconButton label="Estornar" icon={<Undo2 />} tone="danger" onClick={onReverse} />
        ) : null}
      </div>
      {error ? <RequestAlert message={error} /> : null}
    </li>
  );
}

/// A linha da Cobrança online (ADR 0060): sem Confirmar nem Estornar, que vêm do provedor.
function OnlinePaymentRow({
  sale,
  payment,
  charge,
  editable,
  onView,
  onCancel,
}: {
  sale: Sale;
  payment: SalePayment;
  charge: OnlineCharge | undefined;
  editable: boolean;
  onView: (charge: OnlineCharge) => void;
  onCancel: (charge: OnlineCharge) => void;
}) {
  const state = onlinePaymentState(payment, charge);
  const closed = payment.status === 'REVERSED' || payment.status === 'CANCELED';
  return (
    <li className="grid gap-2 rounded-xl border border-surface-border px-3 py-2.5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <strong className="text-sm">{onlinePaymentTitle(charge)}</strong>
          <p className="text-xs text-content-muted">{state.detail}</p>
          {payment.refundedCents ? (
            <p className="text-xs text-content-secondary">
              Recebido:{' '}
              {formatCurrency(payment.receivedAmountCents ?? payment.amountCents, sale.currency)} ·
              Devolvido ao cliente
              {payment.refundMethod
                ? ` por ${REFUND_METHOD_LABELS[payment.refundMethod]}, fora da Cobrança online`
                : ''}
              : {formatCurrency(payment.refundedCents, sale.currency)}
              {payment.amountCents > 0
                ? ` · Retido pela política de cancelamento: ${formatCurrency(payment.amountCents, sale.currency)}`
                : ''}
              .
            </p>
          ) : null}
          {charge?.refundStatus === 'PENDING' ? (
            <p className="text-xs text-content-secondary">
              Devolução pedida ao provedor pelo cancelamento no portal; aguardando a confirmação
              dele. A venda é ajustada quando ela chegar.
            </p>
          ) : payment.status === 'CONFIRMED' &&
            !['AUTHORIZE', 'FAILED', 'MANUAL'].includes(charge?.refundStatus ?? '') ? (
            <p className="text-xs text-content-faint">
              Devolução é feita no provedor; o estorno chega aqui sozinho.
            </p>
          ) : null}
        </div>
        <span
          className={closed ? 'tabular-nums line-through opacity-70' : 'tabular-nums font-semibold'}
        >
          {formatCurrency(payment.amountCents, sale.currency)}
        </span>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <StatusPill tone={state.tone}>{state.label}</StatusPill>
        {AWAITING.has(payment.status) && charge?.status === 'WAITING' ? (
          <div className="flex flex-wrap gap-1">
            <IconButton label="Ver cobrança" icon={<Eye />} onClick={() => onView(charge)} />
            {editable ? (
              <IconButton
                label="Cancelar cobrança"
                icon={<Ban />}
                tone="danger"
                onClick={() => onCancel(charge)}
              />
            ) : null}
          </div>
        ) : null}
      </div>
      {charge?.status === 'PAID_AFTER_CLOSE' ? (
        <RequestAlert message={charge.closedReason ?? 'Cobrança paga depois de encerrada.'} />
      ) : null}
      {charge?.refundStatus === 'AUTHORIZE' ? (
        <RequestAlert message="A devolução ao cliente aguarda a sua autorização no provedor de pagamento. No Asaas, aprove o evento crítico pendente no aviso do topo do painel; o estorno chega aqui sozinho." />
      ) : null}
      {charge?.refundStatus === 'FAILED' ? (
        <RequestAlert message="A devolução ao cliente falhou ou foi recusada no provedor de pagamento. Na Agenda, abra o agendamento cancelado e, no cartão Cancelamento pelo portal, peça a devolução de novo ou registre a devolução feita por outro meio." />
      ) : null}
    </li>
  );
}

/// Escolher, arrastar ou trocar o comprovante antes de gravar o pagamento.
function ReceiptField({
  file,
  onFile,
  required,
  method,
}: {
  file: File | null;
  onFile: (file: File | null) => void;
  required: boolean;
  method: SalePaymentMethod;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  function drop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    onFile(event.dataTransfer.files[0] ?? null);
  }
  return (
    <div className="grid gap-1.5">
      <span className="text-xs font-semibold text-content/70">
        Comprovante{' '}
        {required ? (
          <StatusPill tone="warning">obrigatório para {METHOD_LABELS[method]}</StatusPill>
        ) : (
          <span className="font-normal text-content-muted">(opcional)</span>
        )}
      </span>
      {file ? (
        <div className="flex items-center gap-2 rounded-xl border border-surface-border px-3 py-2 text-sm">
          <FileText className="size-4 shrink-0 text-brand-400" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate">{file.name}</span>
          <IconButton
            label="Tirar o comprovante escolhido"
            icon={<X />}
            onClick={() => onFile(null)}
          />
        </div>
      ) : (
        <div
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={drop}
          className={
            dragging
              ? 'flex flex-wrap items-center gap-3 rounded-xl border border-dashed border-brand-500 bg-brand-500/5 px-3 py-3'
              : 'flex flex-wrap items-center gap-3 rounded-xl border border-dashed border-surface-border px-3 py-3'
          }
        >
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => inputRef.current?.click()}
          >
            <Upload className="size-4" aria-hidden="true" />
            Escolher arquivo
          </Button>
          <span className="text-xs text-content-muted">
            Ou arraste para cá. PDF, JPG, PNG ou WebP, até 10 MB.
          </span>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED}
        className="sr-only"
        aria-label="Arquivo do comprovante"
        onChange={(event) => onFile(event.target.files?.[0] ?? null)}
      />
    </div>
  );
}

function RegisterForm({
  sale,
  today,
  required,
  financeContracted,
  suggestedCents,
  onCancel,
  onSaved,
}: {
  sale: Sale;
  today: string;
  required: SalePaymentMethod[];
  financeContracted: boolean;
  suggestedCents: number;
  onCancel: () => void;
  onSaved: (sale: Sale, cents: number) => Promise<void>;
}) {
  const [method, setMethod] = useState<SalePaymentMethod>('PIX');
  const [mode, setMode] = useState<AmountMode>('BRL');
  const [typed, setTyped] = useState<number | null>(suggestedCents);
  const [basisPoints, setBasisPoints] = useState(10_000);
  /// Em porcentagem, o valor sai do que falta receber (ADR 0059).
  const amount = mode === 'PCT' ? percentOf(suggestedCents, basisPoints) : typed;
  const [paidOn, setPaidOn] = useState<string | null>(today);
  const [reference, setReference] = useState('');
  const [excessReason, setExcessReason] = useState('');
  const excessCents = Math.max(0, (amount ?? 0) - suggestedCents);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!amount || amount <= 0) return setError('Informe o valor recebido.');
    if (excessCents > 0 && !sale.clientId)
      return setError('Identifique o cliente antes de gerar crédito do excedente.');
    if (excessCents > 0 && (excessReason.trim().length < 3 || excessReason.trim().length > 500))
      return setError('Informe um motivo de 3 a 500 caracteres para o crédito excedente.');
    if (!paidOn) return setError('Informe a data do recebimento.');
    if (required.includes(method) && !file)
      return setError(
        `Anexe o comprovante: a empresa exige comprovante para ${METHOD_LABELS[method]}.`,
      );
    setSaving(true);
    setError(undefined);
    try {
      await onSaved(
        await salesApi.registerPayment(sale.id, {
          method,
          amountCents: amount,
          paidOn,
          ...(excessCents > 0 ? { excessCreditReason: excessReason.trim() } : {}),
          externalReference: reference.trim() || null,
          ...(method === 'CARD' && financeContracted ? { card: { installmentCount: 1 } } : {}),
          receipt: file,
        }),
        amount,
      );
    } catch (value) {
      setError(getApiError(value));
      setSaving(false);
    }
  }

  return (
    <FormFrame
      title="Registrar recebimento"
      error={error}
      saving={saving}
      submitLabel="Registrar recebimento"
      onCancel={onCancel}
      onSubmit={() => void submit()}
    >
      <Select
        label="Meio de pagamento"
        value={method}
        options={(Object.keys(METHOD_LABELS) as SalePaymentMethod[])
          .filter((method) => method !== 'CREDIT' && method !== 'ONLINE')
          .map((value) => ({
            value,
            label: METHOD_LABELS[value],
          }))}
        onValueChange={(value) => setMethod(value as SalePaymentMethod)}
      />
      {excessCents > 0 ? (
        <section
          className="grid gap-2 rounded-xl border border-brand-500/30 p-3"
          aria-label="Crédito do excedente"
        >
          <p className="text-sm">
            {formatCurrency(suggestedCents, sale.currency)} quitarão a venda e{' '}
            {formatCurrency(excessCents, sale.currency)} ficarão como crédito do cliente.
          </p>
          <Textarea
            label="Motivo do crédito excedente"
            value={excessReason}
            onChange={(event) => setExcessReason(event.target.value)}
          />
        </section>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <AmountField
          label="Valor recebido"
          currency={sale.currency}
          mode={mode}
          onMode={(next) => {
            if (next === 'PCT') setBasisPoints(basisPointsOf(amount ?? 0, suggestedCents));
            else setTyped(amount);
            setMode(next);
          }}
          cents={amount ?? 0}
          onCents={setTyped}
          basisPoints={basisPoints}
          onBasisPoints={setBasisPoints}
          baseCents={suggestedCents}
          percentAllowed
          hint={`Falta receber ${formatCurrency(suggestedCents, sale.currency)}.`}
        />
        <DatePicker label="Recebido em" value={paidOn} onValueChange={setPaidOn} today={today} />
      </div>
      <Input
        label="Identificação do pagamento (opcional)"
        placeholder="Ex.: ID da transação PIX"
        value={reference}
        maxLength={160}
        onChange={(event) => setReference(event.target.value)}
      />
      <ReceiptField
        file={file}
        onFile={setFile}
        required={required.includes(method)}
        method={method}
      />
    </FormFrame>
  );
}

function ConfirmForm({
  sale,
  payment,
  today,
  required,
  onCancel,
  onSaved,
}: {
  sale: Sale;
  payment: SalePayment;
  today: string;
  required: boolean;
  onCancel: () => void;
  onSaved: (sale: Sale, cents: number) => Promise<void>;
}) {
  const [amount, setAmount] = useState<number | null>(payment.amountCents);
  const [paidOn, setPaidOn] = useState<string | null>(today);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!amount || amount <= 0) return setError('Informe o valor que entrou.');
    if (amount > payment.amountCents)
      return setError(
        'O valor confirmado não pode passar do combinado. Registre o excedente à parte.',
      );
    if (!paidOn) return setError('Informe a data do recebimento.');
    if (required && !file)
      return setError(
        `Anexe o comprovante: a empresa exige comprovante para ${METHOD_LABELS[payment.method]}.`,
      );
    setSaving(true);
    setError(undefined);
    try {
      await onSaved(
        await salesApi.confirmPayment(sale.id, payment.id, {
          amountCents: amount,
          paidOn,
          receipt: file,
        }),
        amount,
      );
    } catch (value) {
      setError(getApiError(value));
      setSaving(false);
    }
  }

  return (
    <FormFrame
      title={`Confirmar ${METHOD_LABELS[payment.method]} de ${formatCurrency(payment.amountCents, sale.currency)}`}
      error={error}
      saving={saving}
      submitLabel="Confirmar recebimento"
      onCancel={onCancel}
      onSubmit={() => void submit()}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <CurrencyInput
          label="Valor que entrou"
          currency={sale.currency}
          value={amount}
          onValueChange={setAmount}
          hint="Se entrou menos, a diferença volta a ficar em aberto."
        />
        <DatePicker label="Recebido em" value={paidOn} onValueChange={setPaidOn} today={today} />
      </div>
      <ReceiptField file={file} onFile={setFile} required={required} method={payment.method} />
    </FormFrame>
  );
}

function ReverseForm({
  sale,
  payment,
  onCancel,
  onSaved,
}: {
  sale: Sale;
  payment: SalePayment;
  onCancel: () => void;
  onSaved: (sale: Sale) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [reversalKind, setReversalKind] = useState<'CORRECTION' | 'ECONOMIC'>('CORRECTION');
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (reason.trim().length < 3) return setError('Informe o motivo do estorno.');
    setSaving(true);
    setError(undefined);
    try {
      await onSaved(
        await salesApi.reversePayment(
          sale.id,
          payment.id,
          reason,
          ...(reversalKind === 'ECONOMIC' ? ([reversalKind] as const) : []),
        ),
      );
    } catch (value) {
      setError(getApiError(value));
      setSaving(false);
    }
  }

  return (
    <FormFrame
      title={`Estornar ${METHOD_LABELS[payment.method]} de ${formatCurrency(payment.receivedAmountCents ?? payment.amountCents, sale.currency)}`}
      error={error}
      saving={saving}
      submitLabel="Estornar pagamento"
      onCancel={onCancel}
      onSubmit={() => void submit()}
    >
      <p className="text-xs text-content-muted">
        O pagamento continua no histórico, riscado, e o valor volta a ficar em aberto.
      </p>
      {payment.method !== 'CREDIT' ? (
        <>
          <SegmentedControl
            label="Tipo do estorno"
            value={reversalKind}
            onChange={setReversalKind}
            options={[
              { value: 'CORRECTION', label: 'Corrigir lançamento' },
              { value: 'ECONOMIC', label: 'Dinheiro devolvido' },
            ]}
          />
          <p className="text-xs text-content-muted">
            {reversalKind === 'CORRECTION'
              ? 'Corrige a receita no dia original. Se já foi conciliada, o excesso precisa ser resolvido na Conciliação.'
              : 'Mantém a receita original e registra a devolução no DRE de hoje. Concilie a saída de dinheiro pelo extrato.'}
          </p>
        </>
      ) : (
        <p className="text-xs text-content-muted">
          Desfaz a receita reconhecida pelo uso do crédito, sem alterar o caixa.
        </p>
      )}
      <Input
        label="Motivo"
        placeholder="Ex.: valor lançado em dobro"
        value={reason}
        maxLength={500}
        onChange={(event) => setReason(event.target.value)}
      />
    </FormFrame>
  );
}

function CreditForm({
  sale,
  today,
  availableCents,
  remainingCents,
  onCancel,
  onSaved,
}: {
  sale: Sale;
  today: string;
  availableCents: number;
  remainingCents: number;
  onCancel: () => void;
  onSaved: (sale: Sale) => Promise<void>;
}) {
  const [amount, setAmount] = useState<number | null>(null);
  const [amountMode, setAmountMode] = useState<AmountMode>('BRL');
  const [amountBp, setAmountBp] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  async function submit() {
    if (!amount || amount <= 0) return setError('Informe quanto crédito deseja usar.');
    if (amount > Math.min(availableCents, remainingCents))
      return setError('O valor ultrapassa o crédito disponível ou o que falta pagar nesta venda.');
    setSaving(true);
    setError(undefined);
    try {
      await onSaved(
        await salesApi.registerPayment(sale.id, {
          method: 'CREDIT',
          amountCents: amount,
          paidOn: today,
        }),
      );
    } catch (value) {
      setError(getApiError(value));
      setSaving(false);
    }
  }
  return (
    <Modal
      open
      title="Usar crédito do cliente"
      description={`${sale.clientDisplayName ?? ''} · Venda ${sale.number} · ${sale.businessUnitName}`}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            Voltar
          </Button>
          <Button onClick={() => void submit()} disabled={saving}>
            Aplicar crédito
          </Button>
        </div>
      }
    >
      <p className="text-sm text-content-muted">
        Disponível nesta unidade: {formatCurrency(availableCents, sale.currency)}. Falta pagar:{' '}
        {formatCurrency(remainingCents, sale.currency)}.
      </p>
      <AmountField
        label="Valor do crédito"
        currency={sale.currency}
        mode={amountMode}
        onMode={(mode) => {
          if (mode === 'PCT') setAmountBp(basisPointsOf(amount ?? 0, availableCents));
          setAmountMode(mode);
        }}
        cents={amount ?? 0}
        onCents={setAmount}
        basisPoints={amountBp}
        onBasisPoints={(bp) => {
          setAmountBp(bp);
          setAmount(percentOf(availableCents, bp));
        }}
        baseCents={availableCents}
        percentAllowed
        hint="O percentual usa o crédito disponível do cliente. O restante fica disponível para outras vendas."
      />
      {error ? <RequestAlert message={error} /> : null}
    </Modal>
  );
}
